import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { StripeService } from 'ngx-stripe';
import { ConfirmCardPaymentData, PaymentIntent, StripeError } from '@stripe/stripe-js';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'src/environments/environment.local';
import { Order, Subscription, ConfirmSubscription, HandleSubscription } from '../models';

interface PaymentIntentResult {
    paymentIntent?: PaymentIntent;
    error?: StripeError;
}

@Injectable({ providedIn: 'root' })
export class StripeAPIService {

    paymentMethodEmitter = new EventEmitter();
    stripeEmitter = new EventEmitter<HandleSubscription>();

    constructor(private http: HttpClient, private stripeService: StripeService) { }

    getAllPlans(): Observable<any> {
        return this.http.get(environment.api.stripe.getAllPlans).pipe(map((res: any) => res.data));
    }

    createOrder(order: Order): Observable<any> {
        return this.http.post(environment.api.stripe.createOrder, order).pipe(map((res: any) => res));
    }

    createSubscription(subcription: Subscription, card: ConfirmCardPaymentData): Observable<any> {
        return this.http.post(
            environment.api.stripe.createSubscription,
            subcription
        ).pipe(map((res: any) => this.handleSubscription(res, card)));
    }

    directPayment(clientSecret: string, id: string): void {
        this.stripeService.confirmCardPayment(clientSecret, { payment_method: id }).subscribe((paymentIntentResult: PaymentIntentResult) => {
            if (paymentIntentResult.error) {
                // The card was declined (i.e. insufficient funds, card has expired, etc)
                switch (paymentIntentResult.error.code) {
                    case 'payment_intent_incompatible_payment_method':
                    case 'payment_intent_invalid_parameter':
                    case 'payment_intent_payment_attempt_failed':
                        paymentIntentResult.error.message = 'error.stripe.subscriptions_payment_refused';
                }
                this.stripeEmitter.emit({ error: paymentIntentResult.error });
            } else {
                if (paymentIntentResult.paymentIntent.status === 'succeeded') {
                    this.stripeEmitter.emit({ success: 1 });
                } else {
                    this.stripeEmitter.emit({ error: { message: 'error.stripe.subscription_not_active' } });
                }
            }
        });
    }

    handleSubscription(subscription: any, card: any): void {
        const { latest_invoice } = subscription;
        const { payment_intent } = latest_invoice;
        if (payment_intent) {
            const { client_secret, status } = payment_intent;
            if (status === 'requires_action' || status === 'requires_payment_method' || status === 'requires_source_action') {
                this.stripeService.confirmCardPayment(
                    client_secret,
                    { payment_method: card.payment_method.id }
                ).subscribe((cardSetupResult: PaymentIntentResult) => {
                    // console.log('5 - cardSetupResult', cardSetupResult);
                    if (cardSetupResult.error) {
                        // The card was declined (i.e. insufficient funds, card has expired, etc)
                        switch (cardSetupResult.error.code) {
                            case 'payment_intent_incompatible_payment_method':
                            case 'payment_intent_invalid_parameter':
                            case 'payment_intent_payment_attempt_failed':
                                cardSetupResult.error.message = 'error.stripe.subscriptions_payment_refused';
                        }
                        this.stripeEmitter.emit({ error: cardSetupResult.error });
                    } else {
                        this.confirmSubscription(subscription.id).subscribe(confirmSubsResult => {
                            // console.log('6 - confirmSubsResult:', confirmSubsResult);
                            if (confirmSubsResult.status === 'active') {
                                this.stripeEmitter.emit({ success: 1 });
                            } else {
                                this.stripeEmitter.emit({ error: { message: 'error.stripe.subscription_not_active' } });
                            }
                        });
                    }
                });
            } else {
                // No additional information was needed
                if (subscription.status === 'active') {
                    // console.log('5 - if status is active');
                    this.stripeEmitter.emit({ success: 1 });
                } else {
                    // console.log('5 - if statis is not active');
                    this.stripeEmitter.emit({ error: { message: 'error.stripe.subscription_not_active' } });
                }
            }
        } else if (subscription.status === 'trialing') {
            // console.log('5 - else trialing');
            this.stripeEmitter.emit({ success: 1 });
        }
    }

    confirmSubscription(subscriptionId: ConfirmSubscription): Observable<any> {
        return this.http.post(environment.api.stripe.confirmSubscription, { subscription_id: subscriptionId }).pipe(map((res: any) => res));
    }

}
